import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

interface LoadingStateProps {
  /** The type of loading state to display */
  variant?: "spinner" | "text" | "skeleton" | "inline";
  /** Size of the loading indicator */
  size?: "sm" | "md" | "lg" | "xl";
  /** Custom loading message */
  message?: string;
  /** Whether to show a subtle animation */
  animated?: boolean;
  /** Custom CSS classes */
  className?: string;
  /** Whether to center the loading state */
  centered?: boolean;
  /** Background color variant */
  background?: "none" | "light" | "dark";
  /** Full height container */
  fullHeight?: boolean;
}

const sizeClasses = {
  sm: "h-4 w-4",
  md: "h-6 w-6",
  lg: "h-8 w-8",
  xl: "h-12 w-12",
};

const messageClasses = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl",
};

const backgroundClasses = {
  none: "",
  light: "bg-card-50",
  dark: "bg-card-900",
};

export function LoadingState({
  variant = "spinner",
  size = "md",
  message = "Loading...",
  animated = true,
  className,
  centered = true,
  background = "none",
  fullHeight = false,
}: LoadingStateProps) {
  const containerClasses = cn(
    "flex items-center justify-center",
    {
      "h-full min-h-[200px]": fullHeight,
      "h-auto": !fullHeight,
    },
    {
      "justify-center": centered,
      "justify-start": !centered,
    },
    backgroundClasses[background],
    className
  );

  const messageClassesApplied = cn("text-gray-600 dark:text-gray-400 font-medium", messageClasses[size], {
    "animate-pulse": animated && variant === "text",
  });

  const spinnerClasses = cn("animate-spin text-gray-500", sizeClasses[size]);

  if (variant === "text") {
    return (
      <div className={containerClasses} role="status" aria-live="polite">
        <span className={messageClassesApplied}>{message}</span>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <div className="flex items-center gap-2" role="status" aria-live="polite">
        <Loader2 className={spinnerClasses} aria-hidden="true" />
        <span className={messageClassesApplied}>{message}</span>
      </div>
    );
  }

  if (variant === "skeleton") {
    return (
      <div
        className={containerClasses}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={message || "Loading content"}
      >
        <div className="space-y-3 w-full max-w-md" aria-hidden="true">
          <div
            className={cn(
              "animate-pulse bg-card-200 rounded",
              size === "sm" ? "h-4" : size === "md" ? "h-6" : size === "lg" ? "h-8" : "h-12"
            )}
          />
          <div
            className={cn(
              "animate-pulse bg-card-200 rounded w-3/4",
              size === "sm" ? "h-3" : size === "md" ? "h-4" : size === "lg" ? "h-5" : "h-6"
            )}
          />
          <div
            className={cn(
              "animate-pulse bg-card-200 rounded w-1/2",
              size === "sm" ? "h-3" : size === "md" ? "h-4" : size === "lg" ? "h-5" : "h-6"
            )}
          />
        </div>
        <span className="sr-only">{message}</span>
      </div>
    );
  }

  // Default spinner variant
  return (
    <div className={containerClasses} role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className={spinnerClasses} aria-hidden="true" />
        {message ? (
          <span className={messageClassesApplied}>{message}</span>
        ) : (
          <span className="sr-only">Loading</span>
        )}
      </div>
    </div>
  );
}

// Convenience components for common use cases
export function LoadingSpinner(props: Omit<LoadingStateProps, "variant">) {
  return <LoadingState variant="spinner" {...props} />;
}

export function LoadingText(props: Omit<LoadingStateProps, "variant">) {
  return <LoadingState variant="text" {...props} />;
}

export function LoadingInline(props: Omit<LoadingStateProps, "variant">) {
  return <LoadingState variant="inline" {...props} />;
}

export function LoadingSkeleton(props: Omit<LoadingStateProps, "variant">) {
  return <LoadingState variant="skeleton" {...props} />;
}
